/** How long a phone or installed app must stay closed before offering a refresh. */
export const STALE_RESUME_REFRESH_MS = 15 * 60 * 1000;
export const PULL_TO_REFRESH_THRESHOLD_PX = 140;
/** Finger travel before a gesture is a scroll, not a pull. */
export const PULL_TO_REFRESH_DIRECTION_SLOP_PX = 10;
export const PULL_TO_REFRESH_RESISTANCE = 0.85;
export const PULL_TO_REFRESH_SETTLE_MS = 280;
export const PULL_TO_REFRESH_MIN_REFRESH_MS = 800;
export const PULL_TO_REFRESH_OFFSET_VAR = "--ap-ptr-offset";
export const PULL_TO_REFRESH_PROGRESS_VAR = "--ap-ptr-progress";
export const PULL_TO_REFRESH_ACTIVE_CLASS = "ap-ptr-active";
export const PULL_TO_REFRESH_SETTLING_CLASS = "ap-ptr-settling";
export const PULL_TO_REFRESH_MARK_SPIN_CLASS = "ap-ptr-mark-spin";
export const PULL_TO_REFRESH_READY_POP_CLASS = "ap-ptr-ready-pop";
export const APP_SPLASH_MIN_VISIBLE_MS = 500;
export const APP_SPLASH_FADE_MS = 450;
export const APP_SPLASH_ELEMENT_ID = "ap-splash";
export const APP_SPLASH_HIDE_CLASS = "ap-splash-hide";
export const APP_SPLASH_HOLD_ATTR = "data-hold";
export const APP_SPLASH_RESUME_SETTLE_MS = 8_000;
export const APP_SPLASH_MAX_HOLD_MS = 12_000;
export const APP_SPLASH_EXPECT_SESSION_KEY = "ap-splash-expect-session";
export const APP_SPLASH_RESUME_START_EVENT = "award-party:resume-refresh-start";
export const APP_SPLASH_RESUME_END_EVENT = "award-party:resume-refresh-end";

export type AppSplashHoldInput = {
  shell: boolean;
  authLoaded: boolean;
  signedIn: boolean;
  sessionPending: boolean;
  expectSession: boolean;
  resumeRefreshing: boolean;
  resumeSettleExpired: boolean;
  signingOut: boolean;
};

type ExpectSessionStorage = {
  getItem: (key: string) => string | null;
  setItem?: (key: string, value: string) => void;
  removeItem?: (key: string) => void;
};

export type PullToRefreshStatus = "idle" | "pulling" | "ready" | "refreshing";

export function shouldRefreshOnResume(
  hiddenAtMs: number | null,
  resumedAtMs: number,
  staleAfterMs: number,
): boolean {
  if (hiddenAtMs === null || staleAfterMs <= 0) {
    return false;
  }
  return resumedAtMs - hiddenAtMs >= staleAfterMs;
}

export function getPullToRefreshPhysics(
  pullDistancePx: number,
  thresholdPx: number,
  resistance: number = PULL_TO_REFRESH_RESISTANCE,
): { offset: number; progress: number; isReady: boolean } {
  if (thresholdPx <= 0) {
    return { offset: 0, progress: 0, isReady: false };
  }

  const pullDistance = Math.max(0, pullDistancePx);
  return {
    offset: pullDistance * resistance,
    progress: pullDistance / thresholdPx,
    isReady: pullDistance >= thresholdPx,
  };
}

export function getPullToRefreshStatus(
  isRefreshing: boolean,
  pullDistancePx: number,
  thresholdPx: number,
): PullToRefreshStatus {
  if (isRefreshing) {
    return "refreshing";
  }
  if (pullDistancePx <= 0) {
    return "idle";
  }
  return pullDistancePx >= thresholdPx ? "ready" : "pulling";
}

export function getPullToRefreshIndicatorState(status: PullToRefreshStatus): {
  spinLogo: boolean;
  armed: boolean;
  glow: boolean;
  rays: boolean;
} {
  const armed = status === "ready";
  const refreshing = status === "refreshing";
  return {
    spinLogo: armed || refreshing,
    armed,
    glow: armed || refreshing,
    rays: armed || refreshing,
  };
}

export function remainingPullToRefreshHoldMs(
  startedAtMs: number,
  nowMs: number,
  minMs: number = PULL_TO_REFRESH_MIN_REFRESH_MS,
): number {
  if (minMs <= 0) {
    return 0;
  }
  return Math.max(0, minMs - (nowMs - startedAtMs));
}

export function reloadCurrentDocument(location: { reload: () => void }): void {
  location.reload();
}

export function isPullToRefreshSurfaceActive(
  status: PullToRefreshStatus,
  settling: boolean,
): boolean {
  return settling || status !== "idle";
}

export function applyPullToRefreshSurface(
  root: HTMLElement,
  {
    offset,
    progress,
    active,
    settling,
  }: {
    offset: number;
    progress: number;
    active: boolean;
    settling: boolean;
  },
): void {
  root.style.setProperty(PULL_TO_REFRESH_OFFSET_VAR, `${offset}px`);
  root.style.setProperty(PULL_TO_REFRESH_PROGRESS_VAR, String(progress));
  root.classList.toggle(PULL_TO_REFRESH_ACTIVE_CLASS, active);
  root.classList.toggle(PULL_TO_REFRESH_SETTLING_CLASS, settling);
}

export function clearPullToRefreshSurface(root: HTMLElement): void {
  root.style.removeProperty(PULL_TO_REFRESH_OFFSET_VAR);
  root.style.removeProperty(PULL_TO_REFRESH_PROGRESS_VAR);
  root.classList.remove(
    PULL_TO_REFRESH_ACTIVE_CLASS,
    PULL_TO_REFRESH_SETTLING_CLASS,
  );
}

export function isStandaloneDisplayMode(
  matches: (query: string) => boolean,
  navigatorStandalone?: boolean,
): boolean {
  return (
    matches("(display-mode: standalone)") ||
    matches("(display-mode: fullscreen)") ||
    matches("(display-mode: minimal-ui)") ||
    matches("(display-mode: window-controls-overlay)") ||
    navigatorStandalone === true
  );
}

export function isElementScrolledFromTop(scrollTop: number): boolean {
  return scrollTop > 1;
}

export function canStartPullToRefresh({
  windowScrollY,
  documentScrollTop,
  ancestorScrolledFromTop,
}: {
  windowScrollY: number;
  documentScrollTop: number;
  ancestorScrolledFromTop: boolean;
}): boolean {
  return (
    !isElementScrolledFromTop(windowScrollY) &&
    !isElementScrolledFromTop(documentScrollTop) &&
    !ancestorScrolledFromTop
  );
}

export function shouldIgnorePullGesture(dx: number, dy: number): boolean {
  return Math.abs(dx) > Math.abs(dy);
}

/**
 * A non-passive touchmove listener is only needed while a pull might steal
 * the gesture. Once the finger is clearly scrolling, drop that listener so
 * the rest of the drag can scroll on the compositor thread.
 */
export function shouldReleasePullToRefreshTracking(
  dx: number,
  dy: number,
  slopPx: number = PULL_TO_REFRESH_DIRECTION_SLOP_PX,
): boolean {
  const slop = Math.max(0, slopPx);
  if (dy < 0 && -dy >= slop) {
    return true;
  }
  return shouldIgnorePullGesture(dx, dy) && Math.hypot(dx, dy) >= slop;
}

export function shouldDismissAppSplash(
  elapsedMs: number,
  minVisibleMs: number,
): boolean {
  return elapsedMs >= minVisibleMs;
}

export function shouldHoldAppSplash({
  shell,
  authLoaded,
  signedIn,
  sessionPending,
  expectSession,
  resumeRefreshing,
  resumeSettleExpired,
  signingOut,
}: AppSplashHoldInput): boolean {
  if (!shell) {
    return false;
  }
  if (resumeRefreshing || !authLoaded || sessionPending) {
    return true;
  }
  if (signingOut) {
    return false;
  }
  return expectSession && !signedIn && !resumeSettleExpired;
}

export function readExpectInstalledSession(
  storage: Pick<ExpectSessionStorage, "getItem"> | null,
): boolean {
  if (!storage) {
    return false;
  }
  try {
    return storage.getItem(APP_SPLASH_EXPECT_SESSION_KEY) === "1";
  } catch {
    return false;
  }
}

export function writeExpectInstalledSession(
  storage: ExpectSessionStorage | null,
  expect: boolean,
): void {
  if (!storage?.setItem || !storage.removeItem) {
    return;
  }
  try {
    if (expect) {
      storage.setItem(APP_SPLASH_EXPECT_SESSION_KEY, "1");
      return;
    }
    storage.removeItem(APP_SPLASH_EXPECT_SESSION_KEY);
  } catch {
    // Private mode and disabled storage should not block the splash.
  }
}

const APP_SPLASH_DEADLINE_ATTR = "data-splash-deadline";
const splashFailsafeTimers = new WeakMap<HTMLElement, number>();

export type RevealAppSplashOptions = {
  maxHoldMs?: number;
};

function readSplashDeadline(splash: HTMLElement): number | null {
  const raw = splash.getAttribute(APP_SPLASH_DEADLINE_ATTR);
  if (!raw) {
    return null;
  }
  const deadline = Number(raw);
  return Number.isFinite(deadline) ? deadline : null;
}

export function hideAppSplash(splash: HTMLElement | null): void {
  if (!splash) {
    return;
  }
  const pending = splashFailsafeTimers.get(splash);
  if (pending !== undefined) {
    window.clearTimeout(pending);
    splashFailsafeTimers.delete(splash);
  }
  splash.removeAttribute(APP_SPLASH_HOLD_ATTR);
  const deadline = readSplashDeadline(splash);
  if (deadline === null || deadline <= Date.now()) {
    splash.removeAttribute(APP_SPLASH_DEADLINE_ATTR);
  }
  splash.classList.add(APP_SPLASH_HIDE_CLASS);
}

export function revealAppSplash(
  splash: HTMLElement | null,
  options?: RevealAppSplashOptions,
): void {
  if (!splash || typeof window === "undefined") {
    return;
  }

  const now = Date.now();
  const maxHoldMs = options?.maxHoldMs ?? APP_SPLASH_MAX_HOLD_MS;
  splash.setAttribute(APP_SPLASH_HOLD_ATTR, "true");
  splash.classList.remove(APP_SPLASH_HIDE_CLASS);

  // Repeat reveals must not push the deadline out. A resume refresh that
  // re-renders, or a holder that remounts, would otherwise leave the splash up.
  const existingDeadline = readSplashDeadline(splash);
  const deadline =
    existingDeadline !== null && existingDeadline > now
      ? existingDeadline
      : now + Math.max(0, maxHoldMs);
  splash.setAttribute(APP_SPLASH_DEADLINE_ATTR, String(deadline));

  const pending = splashFailsafeTimers.get(splash);
  if (pending !== undefined) {
    window.clearTimeout(pending);
  }

  const timer = window.setTimeout(() => {
    splashFailsafeTimers.delete(splash);
    if (splash.getAttribute(APP_SPLASH_DEADLINE_ATTR) !== String(deadline)) {
      return;
    }
    hideAppSplash(splash);
  }, Math.max(0, deadline - now));
  splashFailsafeTimers.set(splash, timer);
}

export function pathHasScrolledAncestor(scrollTops: number[]): boolean {
  return scrollTops.some((scrollTop) => isElementScrolledFromTop(scrollTop));
}
